import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import {
  FeatherAlertCircle,
  FeatherClock,
  FeatherFileText,
  FeatherLoader,
  FeatherWifiOff,
} from "@subframe/core";
import { Link } from "@tanstack/react-router";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import type { AcknowledgementRequestWithSync } from "@/hooks/use-offline-my-acknowledgements";
import { ACK_COPY } from "@/lib/acknowledgements/copy";
import { itemTypeMeta } from "@/lib/acknowledgements/item-types";
import { dueLabel } from "@/lib/acknowledgements/my-acknowledgements";
import { canOpenOffline } from "@/lib/acknowledgements/offline-availability";
import { acknowledgementRequestPill } from "@/lib/workspace/status-pill";

interface AcknowledgementRequestRowProps {
  request: AcknowledgementRequestWithSync;
  isOffline?: boolean;
}

/**
 * A document the staff member has to read and sign, on their To do list (AC10).
 *
 * Structurally the same as FollowUpActionRow, with one behavioural difference:
 * when the device is offline and this document was never cached, the row is
 * shown but NOT openable. Hiding it would look like the obligation had gone
 * away; letting them sign it would record that they read something they could
 * not see, into a record nobody can correct.
 */
export function AcknowledgementRequestRow({
  request,
  isOffline,
}: AcknowledgementRequestRowProps) {
  const meta = itemTypeMeta(request.itemType);
  const due = dueLabel(request, new Date());
  const openable = canOpenOffline(
    Boolean(isOffline),
    Boolean(request.hasCachedDocument),
  );

  let syncBadge: React.ReactNode | null = null;
  if (request.localSyncStatus) {
    const { pendingCount, failedCount } = request.localSyncStatus;
    if (failedCount > 0) {
      syncBadge = (
        <Badge variant="error" icon={<FeatherAlertCircle />}>
          Sync failed
        </Badge>
      );
    } else if (pendingCount > 0) {
      syncBadge = !isOffline ? (
        <Badge
          variant="neutral"
          icon={<FeatherLoader className="animate-spin" />}
        >
          Syncing
        </Badge>
      ) : (
        <Badge variant="warning">Pending sync</Badge>
      );
    }
  }

  const body = (
    <div className="flex w-full items-center justify-between gap-4 rounded-sm px-4 py-3 mobile:items-start mobile:gap-3 mobile:rounded-none mobile:px-4 mobile:py-4">
      <div className="flex items-center gap-3 grow shrink-0 basis-0 max-w-[50%]">
        <TintedEntityIcon
          entity={meta.entity}
          size={40}
          fallbackIcon={<FeatherFileText />}
        />
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-body-bold font-body-bold text-neutral-900 mobile:text-large-button mobile:font-large-button truncate w-full">
            {request.title}
          </span>
          <div className="flex items-center gap-1 mobile:gap-1.5">
            <FeatherClock
              className={`text-caption font-caption mobile:text-large-button mobile:font-large-button ${due.overdue ? "text-error-700" : "text-subtext-color"}`}
            />
            <span
              className={`text-caption font-caption mobile:text-body mobile:font-body ${due.overdue ? "text-error-700" : "text-subtext-color"}`}
            >
              {due.text}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 flex-shrink-0 mobile:flex-col mobile:items-end mobile:gap-2">
        {!openable ? (
          <Badge variant="neutral" icon={<FeatherWifiOff />}>
            {ACK_COPY.ROW_NEEDS_CONNECTION}
          </Badge>
        ) : (
          syncBadge || (
            <WorkspaceStatusBadge pill={acknowledgementRequestPill(request)} />
          )
        )}
        {openable && (
          <Button className="mobile:hidden" variant="neutral-primary">
            {ACK_COPY.ROW_ACTION}
          </Button>
        )}
      </div>
    </div>
  );

  if (!openable) {
    // No Link wrapper: tapping through would land on a page that cannot render
    // the document, which reads as a broken app rather than a missing network.
    return (
      <div
        className="block opacity-70"
        data-testid={`ack-request-row-${request.requestId}`}
      >
        {body}
      </div>
    );
  }

  return (
    <Link
      to="/acknowledgements/$requestId"
      params={{ requestId: request.requestId }}
      className="block hover:bg-neutral-50 transition-colors"
      data-testid={`ack-request-row-${request.requestId}`}
    >
      {body}
    </Link>
  );
}
